// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { Form, Input, Select, InputNumber, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  formateDateTime,
  handleDelete,
  pagConfiguration,
  paginationOptions,
} from "../../utils/Constants";
import {
  addProjectCodeThunk,
  deleteProjectCodeThunk,
  editProjectCodeThunk,
  getApproverListThunk,
  getProjectCodeListThunk,
  searchProjectCodesThunk,
} from "../../store/thunks/projectCodesThunk";
import media from "../../media";
import EditForm from "../../components/molecules/Forms/editForm";
import { selectedRowInfo } from "../../store/slices/authSlice";
import { useSearchAndPagination } from "../../utils/useSearchAndPagination";
import { getDepartmentListThunk } from "../../store/thunks/masterDataThunk";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  departments,
  approvers,
  ...restProps
}) => {
  let inputNode;
  if (dataIndex === "departmentID") {
    inputNode = (
      <Select
        isclearable="true"
        options={departments}
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else if (dataIndex === "approvers") {
    inputNode = (
      <Select
        mode={"multiple"}
        isclearable="true"
        options={approvers}
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else {
    inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  }
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function ProjectCode() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const projectCodes = useSelector((state) => state.projectCodes.list) || [];
  const projectCodesLoading = useSelector(
    (state) => state.projectCodes.loading
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filteredProjectCodes, setFilteredProjectCodes] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProjectCode, setEditingProjectCode] = useState(null);
  const [departments, setDepartments] = useState([]),
    [approvers, setApprovers] = useState([]);
  const [addProjectCode, setAddProjectCode] = useState({
    departmentID: "",
    approvers: [],
    code: "",
    clientName: "",
    companyName: "",
    history: "",
    status: "",
    createdAt: "",
    updatedAt: "",
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch projectCodes, approvers, departments
  const fetchProjectCodes = async () => {
    const res = await dispatch(
      getProjectCodeListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();

    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchProjectCodes();

    dispatch(getApproverListThunk())
      .unwrap()
      .then((res) => {
        setApprovers(res || []); // update local state
      })
      .catch((err) => console.error(err));

    dispatch(getDepartmentListThunk())
      .unwrap()
      .then((res) => {
        setDepartments(res || []); // update local state
      })
      .catch((err) => console.error(err));
  }, [currentPath, dispatch]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingProjectCode(record); // store the record to edit
    tableForm.setFieldsValue({
      departmentID: record.departmentID,
      code: record.code,
      clientName: record.clientName,
      companyName: record.companyName,
      approvers: record.approvers,
      status: record?.status,
    });
    setIsEditModalOpen(true); // open the modal
  };

  const { handleGlSearch, handlePaginationChange, pagination } =
    useSearchAndPagination({
      searchThunk: searchProjectCodesThunk,
      listThunk: getProjectCodeListThunk,
      currentPath,
      role: storeRole,
      lIUId: storeLIUId,
      setFilteredData: setFilteredProjectCodes,
      setStoreTotalCount,
    });

  const effectiveList = filteredProjectCodes.length
    ? filteredProjectCodes
    : projectCodes;

  const onPageChange = async (page, pageSize, currentSearchText = "") => {
    const trimmedSearch = currentSearchText.trim();
    setShowLoader(true);

    try {
      if (trimmedSearch) {
        await handleGlSearch({
          value: trimmedSearch,
          page,
          pageSize,
        });
      } else {
        await handlePaginationChange(page, pageSize);
      }
    } finally {
      setShowLoader(false); // <-- Always runs when request is done
    }
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
      className: "all-nowrap-column",
    },
    {
      title: "Department",
      dataIndex: "departmentID",
      editable: true,
      render: (text) => {
        const [code = "", companyName = ""] =
          (text && text?.split(" - ")) || [];

        return (
          <>
            <div>{code}</div>
            <small>{companyName}</small>
          </>
        );
      },
    },
    {
      title: "Code",
      dataIndex: "code",
      editable: true,
    },
    {
      title: "Client Name",
      dataIndex: "clientName",
      className: "all-nowrap-column",
      editable: true,
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.companyName.localeCompare(b.companyName),
    },
    {
      title: "Approvers",
      dataIndex: "approvers",
      editable: false,
      className: "all-nowrap-column",
      render: (approvers, record) => {
        if (!approvers || !Array.isArray(approvers) || approvers.length === 0) {
          return <span>-</span>;
        }

        return (
          <div style={{ display: "flex", gap: "8px" }}>
            {approvers.map((a, idx) => (
              <Tooltip
                key={idx}
                title={`${a?.name.toUpperCase()}`}
                className={`approvers-status-tooltip mr-5`}
              >
                <span>
                  {a?.profilePicUrl ? (
                    <>
                      <img
                        src={
                          process.env.REACT_APP_BACKEND +
                          "/images/" +
                          a?.profilePicUrl
                        }
                        alt="card icon"
                      />
                    </>
                  ) : (
                    <span className="initialLetter">
                      {a?.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </span>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      render: (text) => (
        <>
          <span className={`${text?.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      className: "all-nowrap-column",
      render: (text) => text && formateDateTime(text),
    },

    ...(storeRole === process.env.REACT_APP_SROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            className: "all-nowrap-column",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",

            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        departments: departments,
        approvers: approvers,
      }),
    };
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Department",
      name: "departmentID",
      rules: [
        {
          required: true,
          message: "Please select department",
        },
      ],
      placeholder: "Select Department",
      type: "select",
      options: departments,

      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Approvers",
      name: "approvers",
      rules: [
        {
          required: true,
          message: "Please select approver",
        },
      ],
      placeholder: "Select Approver",
      type: "select",
      multiple: true,
      options: approvers,
      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Client Name",
      name: "clientName",
      rules: [
        {
          required: true,
          message: "Please enter client name",
        },
      ],
      placeholder: "Enter the client name",
      type: "input",
      onChange: (e) => {
        setAddProjectCode({
          ...addProjectCode,
          clientName: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Client Code",
      name: "code",
      rules: [
        {
          required: true,
          message: "Please enter client code",
        },
      ],
      placeholder: "Enter the client code",
      type: "input",
      onChange: (e) => {
        setAddProjectCode({
          ...addProjectCode,
          code: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Company Name",
      name: "companyName",
      rules: [
        {
          required: true,
          message: "Please enter company name",
        },
      ],
      placeholder: "Enter the company name",
      type: "input",
      onChange: (e) => {
        setAddProjectCode({
          ...addProjectCode,
          companyName: e.target.value,
        });
      },
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Project Code"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name={storeRole === process.env.REACT_APP_SROLE ? "Add New Code" : ""}
          onButtonClick={() => setIsAddModalOpen(true)}
          onFilteredData={setFilteredProjectCodes}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            route: currentPath,
            departmentID: values?.departmentID,
            approvers: values?.approvers,
            code: values?.code,
            clientName: values?.clientName,
            companyName: values?.companyName,
            status: values?.status,
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
          })}
          list={addProjectCodeThunk}
          formFields={formFields}
          successMessage="Project Code added successfully." //SuperAdmin and Admin can do
          onSuccess={fetchProjectCodes}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteProjectCodeThunk,
              route: currentPath,
              actionName: "projectCode",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          isGlSearch={true}
          //onSearchValue={(value) => handleGlSearch({ value })}
          onSearchValue={(value) => {
            setShowLoader(true);

            handleGlSearch({ value }).finally(() => setShowLoader(false));
          }}
          searchClassName="table-search dashboardSearch"
          searchPlaceholder="Search Department, Code, Client Name & Company Name"
          searchData={projectCodes}
          searchKeys={["department", "code", "clientName", "companyName"]}
          searchText={searchText}
          setSearchText={setSearchText}
        />

        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={
                      storeRole === process.env.REACT_APP_SROLE && rowSelection
                    }
                    storeList={filteredProjectCodes}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={pagination}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    searchText={searchText}
                    handlePaginationChange={handlePaginationChange}
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>

        <EditForm
          title="Edit Project Code"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Department Name",
              name: "departmentID",
              rules: [
                { required: true, message: "Please input expense name!" },
              ],
              type: "select",
              options: departments,
            },
            { label: "Code", name: "code", type: "input" },
            { label: "Client Name", name: "clientName", type: "input" },
            { label: "Company Name", name: "companyName", type: "input" },
            {
              label: "Approvers",
              name: "approvers",
              type: "select",
              options: approvers,
              props: {
                mode: "multiple",
                placeholder: "Select approvers",
              },
            },
            {
              label: "Status",
              name: "status",
              type: "select",
              options: [
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
                { label: "Softdelete", value: "SOFTDELETE" },
              ],
            },
          ]}
          submitThunk={editProjectCodeThunk}
          payloadBuilder={(values) => {
            const payload = {
              route: currentPath,
              id: editingProjectCode.key,
              code: values?.code,
              clientName: values?.clientName,
              companyName: values?.companyName,
              status: values?.status,
              historySource: "backend",
              historyAction: "update",
              historyUserID: storeLIUId,
            };
            // Only include department if it has been edited
            if (values?.departmentID !== editingProjectCode?.departmentID) {
              payload.departmentID = values?.departmentID;
            }

            // Only include approvers if it has been edited
            if (values?.approvers !== editingProjectCode?.approvers) {
              payload.approvers = values?.approvers;
            }
            return payload;
          }}
          successMessage="Project Code updated successfully."
          currentPath={currentPath}
          refreshListThunk={getProjectCodeListThunk}
        />
      </div>
    </div>
  );
}
